import { db, timestamp } from '../../firebase/config';
import { client } from '../../hooks/Client';
import notDeleted from '../notDeleted';
import { v4 as uuid } from 'uuid';
import readHouseholdField from './householdFields';

/**
 * @typedef {object} DefaultReminderResult
 * @property {Array<'email'|'sms'>} created - Per kanaal dat geschreven is
 * @property {Array<{channel: 'email'|'sms', reason: 'no-email'|'no-phone'|'no-sms-consent'}>} skipped - Wat niet kon, en waarom
 * @property {'no-address'|'exists'} [reason] - Waarom er helemaal niets gebeurd is
 */

/**
 * Maakt bij een zojuist ingepland huisbezoek de automatische herinnering(en)
 * voor het huishouden aan: per e-mail en/of per SMS, zoals de organisatie het
 * heeft ingesteld (`resolveDefaultReminderPlan`). Leest e-mailadres en
 * telefoonnummer vers uit het huishouden (en de oude regels), zodat de beslissing op de echte
 * gegevens rust en niet op een oude momentopname van een hook.
 *
 * Een SMS gaat ALLEEN met de toestemming van het huishouden zelf
 * (`Adresses.SmsConsent`, gezet op het tabblad Gegevens). De verzender eist
 * `SmsConsent === true` op de herinnering; die vlag hier zonder grond zetten
 * zou een sms sturen die niemand heeft goedgekeurd — en elke sms kost geld.
 *
 * Wat niet kan wordt overgeslagen en teruggemeld, nooit stil: een herinnering
 * zonder adres of nummer zou nooit vertrekken en niemand ziet dat.
 *
 * AI-CONTEXT: See docs/features/default-housevisit-reminders.md
 *
 * @param {object} params
 * @param {object|null|undefined} params.task - De opgeslagen afspraak (ID + AdressID)
 * @param {string} [params.authId] - Wie inplant (CreatedBy)
 * @param {{channels: Array<'email'|'sms'>, daysBefore: number}} [params.plan] - Uit resolveDefaultReminderPlan; zonder: e-mail, één dag van tevoren
 * @returns {Promise<DefaultReminderResult>}
 */
const createDefaultHouseVisitReminder = async ({ task, authId, plan } = {}) => {
    const channels = Array.isArray(plan?.channels) && plan.channels.length > 0 ? plan.channels : ['email'];
    const daysBefore = Number.isInteger(plan?.daysBefore) ? plan.daysBefore : 1;

    // Guard the .where() calls against undefined ID/AdressID (Firestore rejects
    // undefined query values) and an empty client.
    if (!task?.ID || !task?.AdressID || !client) {
        return { created: [], skipped: [], reason: 'no-address' };
    }

    // Don't double-create — a re-confirm or a second save must not stack reminders.
    //
    // `CompagnyID` MOET in de zoekopdracht staan, ook al is TaskID een uuid en
    // dus al uniek. De Firestore-regel op TaskReminders is `isTeam()`, en die
    // kijkt naar `resource.data.CompagnyID`. Een zoekopdracht zonder dat filter
    // kán in theorie een document van een andere organisatie raken, en dan
    // weigert Firestore de HELE zoekopdracht — geen lege uitkomst maar
    // 'Missing or insufficient permissions', midden in het opslaan van een
    // huisbezoek. Tot 2026-09-21 viel dat niet op omdat deze functie alleen
    // liep bij een vervolgbezoek; sinds je een huishouden vanuit de lijst de
    // agenda in kunt slepen is het het gewone pad.
    // AI-CONTEXT: See docs/pitfalls.md#database-missing-compagnyid-on-fresh-fetch
    //
    // Geen `.limit(1)`: een zacht verwijderde herinnering telt niet mee, en die
    // filteren we hier met de hand — een directe lees komt niet langs de engine
    // die dat voor de hooks doet. Met een limiet zou één verwijderde rij het
    // huishouden voorgoed zonder herinnering laten.
    const existing = await db.collection('TaskReminders')
        .where('CompagnyID', '==', client)
        .where('TaskID', '==', task.ID)
        .get();
    if (existing.docs.some((doc) => notDeleted(doc.data()))) {
        return { created: [], skipped: [], reason: 'exists' };
    }

    // E-mail en telefoon zijn vaste gegevens van het huishouden; tot de
    // migratie vullen de oude PersonaData-regels aan. `readHouseholdField`
    // plakt nooit twee nummers aan elkaar (bug 1: resolvePrimaryFieldValue
    // deed dat wel) en controleert een oud e-mailadres.
    // AI-CONTEXT: See docs/features/huishouden-vaste-gegevens.md (T7)
    const snap = await db.collection('PersonaData')
        .where('CompagnyID', '==', client)
        .where('PersonaID', '==', task.AdressID)
        .get();

    /** @type {Array<object>} */
    const rows = [];
    snap.forEach((doc) => {
        const d = doc.data();
        if (notDeleted(d)) rows.push(d);
    });

    /** Het huishouden zelf: vaste gegevens en de SMS-toestemming. */
    const adress = await leesHuishouden(task.AdressID);
    const bron = adress || { ID: task.AdressID };
    const email = readHouseholdField(bron, rows, 'Email') || '';
    const phone = readHouseholdField(bron, rows, 'Telefoon') || '';

    const basis = {
        TaskID: task.ID,
        DaysBefore: daysBefore,
        Recipient: '',
        RecipientID: task.AdressID,
        CreatedBy: authId || 'unknown',
        CreatedAt: timestamp,
        CompagnyID: client,
        Compagny: client,
        Sent: false,
    };

    /** @type {DefaultReminderResult} */
    const result = { created: [], skipped: [] };

    for (const channel of channels) {
        if (channel === 'email') {
            if (!email) { result.skipped.push({ channel, reason: 'no-email' }); continue; }
            await db.collection('TaskReminders').add({
                ...basis,
                ID: uuid(),
                Type: 'email',
                ContactInfo: email,
                Position: result.created.length + 1,
            });
            result.created.push('email');
        } else if (channel === 'sms') {
            if (!phone) { result.skipped.push({ channel, reason: 'no-phone' }); continue; }
            if (adress?.SmsConsent !== true) { result.skipped.push({ channel, reason: 'no-sms-consent' }); continue; }
            await db.collection('TaskReminders').add({
                ...basis,
                ID: uuid(),
                Type: 'sms',
                ContactInfo: phone,
                // De toestemming reist mee van het huishouden naar de
                // herinnering: wie hem gaf en wanneer, zodat de tab
                // 'Herinneringen' en de verzender dezelfde grond zien.
                SmsConsent: true,
                SmsConsentBy: adress.SmsConsentBy || authId || 'unknown',
                SmsConsentAt: adress.SmsConsentAt || timestamp,
                Position: result.created.length + 1,
            });
            result.created.push('sms');
        }
    }

    return result;
};

/**
 * Het huishouden binnen de eigen organisatie, op zijn `ID`-veld.
 * AI-CONTEXT: See docs/pitfalls.md#database-missing-compagnyid-on-fresh-fetch
 * @param {string} adressId
 * @returns {Promise<object|null>}
 */
const leesHuishouden = async (adressId) => {
    const snap = await db.collection('Adresses')
        .where('CompagnyID', '==', client)
        .where('ID', '==', adressId)
        .get();
    const doc = snap.docs.find((d) => notDeleted(d.data()));
    return doc ? doc.data() : null;
};

/** Wat de planner leest per overgeslagen kanaal. */
const NOTICES = {
    'no-email': 'Geen e-mailadres bekend — e-mailherinnering niet aangemaakt',
    'no-phone': 'Geen telefoonnummer bekend — SMS-herinnering niet aangemaakt',
    'no-sms-consent': 'Geen SMS-toestemming bij dit huishouden — SMS-herinnering niet aangemaakt',
};

/**
 * De melding voor de planner na het inplannen: alleen wat niet gelukt is. Wat
 * wél lukte staat gewoon in de tab 'Herinneringen'. Niets te melden = null.
 * @param {DefaultReminderResult} [result]
 * @returns {string|null}
 */
const defaultReminderNotice = (result) => {
    const skipped = Array.isArray(result?.skipped) ? result.skipped : [];
    const regels = skipped.map((s) => NOTICES[s.reason]).filter(Boolean);
    return regels.length > 0 ? regels.join('. ') : null;
};

export { defaultReminderNotice };
export default createDefaultHouseVisitReminder;
